// src/services/chatService.ts - Version JSONB System avec calcul frontend des timestamps

import {
  Message,
  Conversation,
  ApiResponse,
  ChatServiceConfig,
  MessageValidationResult,
  SendMessageOptions
} from '../types/index';

export interface ToggleAutoAIPayload {
  conversation_id: string;
  phone_number: string;
  auto_ai_enabled: boolean;
  action: 'toggle_auto_ai';
  timestamp: string;
  user_agent?: string;
}

export interface SendManualSMSPayload {
  phone_number: string;
  message: string;
  conversation_id: string;
  timestamp: string;
  action: 'send_manual_sms';
  user_agent?: string;
}

export class ChatService {
  public config: ChatServiceConfig;

  constructor(config: ChatServiceConfig) {
    this.config = config;
    console.log('[ChatService] Initialized with JSONB system configuration');
  }

  /**
   * Gets secured headers - can be overridden by SecuredChatService
   */
  protected getSecuredHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'User-Agent': navigator.userAgent,
    };
  }

  /**
   * Validates a message before sending
   */
  public validateMessage(message: string): MessageValidationResult {
    const trimmed = message.trim();
    
    if (trimmed.length === 0) {
      return { isValid: false, error: 'Le message ne peut pas être vide.' };
    }
    
    if (trimmed.length > 4000) {
      return { isValid: false, error: 'Le message ne peut pas dépasser 4000 caractères.' };
    }

    // Vérification de caractères potentiellement problématiques
    if (trimmed.includes('\0')) {
      return { isValid: false, error: 'Le message contient des caractères invalides.' };
    }
    
    return { isValid: true };
  }

  /**
   * Validates API response structure
   */
  public validateApiResponse(data: any): data is ApiResponse {
    return typeof data === 'object' && 
           data !== null && 
           (typeof data.success === 'boolean' || Array.isArray(data.data));
  }

  /**
   * Handles API errors consistently
   */
  public handleApiError(error: any): string {
    console.error('[ChatService] API Error (JSONB system):', error);
    
    if (error.name === 'AbortError') {
      return 'Timeout: La requête a pris trop de temps à répondre.';
    }
    
    if (error.message) {
      // Nettoyer les messages d'erreur techniques pour l'utilisateur
      if (error.message.includes('fetch')) {
        return 'Erreur de connexion au serveur. Vérifiez votre connexion internet.';
      }
      if (error.message.includes('JSON')) {
        return 'Erreur de format de données du serveur.';
      }
      return error.message;
    }
    
    return 'Une erreur est survenue lors de la communication avec le serveur.';
  }

  /**
   * Creates a new message object (for optimistic UI updates)
   */
  public createMessage(text: string, conversationId: string, isOutgoing: boolean = true): Message {
    return {
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      text: text.trim(),
      timestamp: new Date().toISOString(),
      isOutgoing,
      status: isOutgoing ? 'sent' : undefined,
      conversationId
    };
  }

  /**
   * Makes a standardized API request
   */
  protected async makeApiRequest(url: string, options: RequestInit = {}): Promise<ApiResponse> {
    const defaultOptions: RequestInit = {
      headers: this.getSecuredHeaders(),
      signal: AbortSignal.timeout(this.config.apiTimeout),
    };

    const mergedOptions = {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...options.headers,
      },
    };

    try {
      console.log('[ChatService] Making API request to (JSONB system):', url);
      console.log('[ChatService] Request options:', {
        method: mergedOptions.method || 'GET',
        hasBody: !!mergedOptions.body,
        headers: mergedOptions.headers
      });
      
      const response = await fetch(url, mergedOptions);

      console.log('[ChatService] Response status (JSONB):', response.status);
      console.log('[ChatService] Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[ChatService] Error response body (JSONB):', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const rawData = await response.json();
      console.log('[ChatService] Raw response data received (JSONB system)');
      console.log('[ChatService] RAW DATA:', JSON.stringify(rawData, null, 2));
      
      const adaptedData = this.parseApiResponse(rawData);
      console.log('[ChatService] Data parsing completed (JSONB):', {
        conversationsCount: adaptedData.conversations?.length || 0,
        messagesCount: adaptedData.messages?.length || 0
      });

      return adaptedData;
    } catch (error: any) {
      console.error('[ChatService] API request failed (JSONB system):', error);
      throw new Error(this.handleApiError(error));
    }
  }

  /**
   * Makes a specific API request for marking conversations as read
   */
  private async makeMarkAsReadRequest(payload: any): Promise<{ success: boolean; message?: string }> {
    const options: RequestInit = {
      method: 'POST',
      headers: this.getSecuredHeaders(), // UPDATED: Use secured headers
      body: JSON.stringify(payload),
      signal: AbortSignal.timeout(this.config.apiTimeout),
    };

    try {
      console.log('[ChatService] Making mark as read request (JSONB):', this.config.markAsReadUrl);
      console.log('[ChatService] Payload:', payload);
      console.log('[ChatService] Headers with security:', options.headers);
      
      const response = await fetch(this.config.markAsReadUrl, options);

      console.log('[ChatService] Mark as read response status (JSONB):', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[ChatService] Mark as read error response (JSONB):', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('[ChatService] Mark as read response (JSONB):', result);
      
      return {
        success: true,
        message: result.message || 'Conversation marquée comme lue'
      };
    } catch (error: any) {
      console.error('[ChatService] Mark as read request failed (JSONB):', error);
      throw new Error(this.handleApiError(error));
    }
  }

  /**
   * Makes a specific API request for sending manual SMS with JSONB system
   */
  private async makeSendSMSRequest(payload: SendManualSMSPayload): Promise<{ success: boolean; message?: string; message_id?: string }> {
    const options: RequestInit = {
      method: 'POST',
      headers: this.getSecuredHeaders(), // UPDATED: Use secured headers
      body: JSON.stringify(payload),
      signal: AbortSignal.timeout(this.config.apiTimeout),
    };

    try {
      console.log('[ChatService] Making send SMS request (JSONB system):', this.config.sendSMSUrl);
      console.log('[ChatService] SMS Payload (JSONB):', payload);
      console.log('[ChatService] Headers with security:', options.headers);
      
      const response = await fetch(this.config.sendSMSUrl, options);

      console.log('[ChatService] Send SMS response status (JSONB):', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[ChatService] Send SMS error response (JSONB):', errorText);
        
        // Gestion des erreurs spécifiques
        if (response.status === 429) {
          throw new Error('Trop de messages envoyés. Veuillez patienter quelques instants.');
        }
        if (response.status === 400) {
          throw new Error('Données du message invalides.');
        }
        if (response.status === 500) {
          throw new Error('Erreur serveur lors de l\'envoi du SMS.');
        }
        
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('[ChatService] Send SMS response (JSONB system):', result);
      
      return {
        success: true,
        message: result.message || 'SMS envoyé avec succès (JSONB)',
        message_id: result.message_id
      };
    } catch (error: any) {
      console.error('[ChatService] Send SMS request failed (JSONB system):', error);
      throw new Error(this.handleApiError(error));
    }
  }

  /**
   * Makes a specific API request for toggling Auto AI
   */
  private async makeAutoAIRequest(payload: ToggleAutoAIPayload): Promise<{ success: boolean; message?: string }> {
    const options: RequestInit = {
      method: 'POST',
      headers: this.getSecuredHeaders(), // UPDATED: Use secured headers
      body: JSON.stringify(payload),
      signal: AbortSignal.timeout(this.config.apiTimeout),
    };

    try {
      console.log('[ChatService] Making Auto AI toggle request (JSONB):', this.config.autoAIUrl);
      console.log('[ChatService] Payload:', payload);
      console.log('[ChatService] Headers with security:', options.headers);
      
      const response = await fetch(this.config.autoAIUrl, options);

      console.log('[ChatService] Auto AI toggle response status (JSONB):', response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[ChatService] Auto AI toggle error response (JSONB):', errorText);
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('[ChatService] Auto AI toggle response (JSONB):', result);
      
      return {
        success: true,
        message: result.message || 'Auto AI mis à jour'
      };
    } catch (error: any) {
      console.error('[ChatService] Auto AI toggle request failed (JSONB):', error);
      throw new Error(this.handleApiError(error));
    }
  }

  /**
   * NOUVEAU: Calcule le vrai timestamp du dernier message d'une conversation
   */
  private calculateRealLastTimestamp(conversationId: string, messages: any[]): string | null {
    if (!Array.isArray(messages) || messages.length === 0) {
      return null;
    }

    // Filtrer les messages de cette conversation
    const conversationMessages = messages.filter(msg => msg.conversation_id === conversationId);
    
    if (conversationMessages.length === 0) {
      return null;
    }

    // Trier par timestamp et prendre le dernier
    const sortedMessages = conversationMessages.sort((a, b) => {
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();
      return timeB - timeA; // Tri décroissant
    });

    const lastMessage = sortedMessages[0];
    console.log(`[ChatService] Real last message for ${conversationId}:`, {
      messageId: lastMessage.id,
      timestamp: lastMessage.timestamp,
      text: lastMessage.text?.substring(0, 50) + '...'
    });

    return this.formatTimestamp(lastMessage.timestamp);
  }

  /**
   * NOUVEAU: Calcule le vrai dernier message texte d'une conversation
   */
  private calculateRealLastMessage(conversationId: string, messages: any[]): string {
    if (!Array.isArray(messages) || messages.length === 0) {
      return 'Aucun message';
    }

    const conversationMessages = messages.filter(msg => msg.conversation_id === conversationId);
    
    if (conversationMessages.length === 0) {
      return 'Aucun message';
    }

    const sortedMessages = conversationMessages.sort((a, b) => {
      const timeA = new Date(a.timestamp).getTime();
      const timeB = new Date(b.timestamp).getTime();
      return timeB - timeA;
    });

    return sortedMessages[0].text || 'Message vide';
  }

  /**
   * Parses API response from JSONB structure
   * Updated to handle messages stored in JSONB column within conversations
   * MODIFIÉ: Utilise le calcul frontend des timestamps
   */
  private parseApiResponse(rawData: any): ApiResponse {
    console.log('[ChatService] Parsing API response structure (JSONB system)');

    // Handle PostgreSQL json_build_object wrapper: {json_build_object: {response: {data: {...}}}}
    if (rawData.json_build_object && rawData.json_build_object.response && 
        rawData.json_build_object.response.data && 
        rawData.json_build_object.response.data.conversations && 
        rawData.json_build_object.response.data.messages) {
      
      console.log('[ChatService] Processing PostgreSQL json_build_object structure (JSONB)');
      const apiData = rawData.json_build_object.response.data;
      
      const conversations: Conversation[] = [];
      const messages: Message[] = [];

      // Parse conversations avec calcul frontend des vrais timestamps
      if (Array.isArray(apiData.conversations)) {
        apiData.conversations.forEach((conv: any) => {
          // NOUVEAU: Calculer le vrai timestamp et message depuis les données des messages
          const realTimestamp = this.calculateRealLastTimestamp(conv.id, apiData.messages) || this.formatTimestamp(conv.last_message_timestamp);
          const realLastMessage = this.calculateRealLastMessage(conv.id, apiData.messages) || conv.last_message || 'Aucun message';
          
          console.log(`[ChatService] Timestamp comparison for ${conv.phone_number}:`, {
            apiTimestamp: conv.last_message_timestamp,
            calculatedTimestamp: realTimestamp,
            apiLastMessage: conv.last_message,
            calculatedLastMessage: realLastMessage.substring(0, 50) + '...'
          });

          const conversation: Conversation = {
            id: conv.id,
            phoneNumber: conv.phone_number,
            lastMessage: realLastMessage, // Utiliser le vrai dernier message
            timestamp: realTimestamp, // Utiliser le vrai timestamp calculé
            unreadCount: conv.unread_count || 0,
            isActive: conv.is_active || false,
            isOnline: conv.is_online || false,
            autoAiEnabled: conv.auto_ai_enabled || false,
            isRead: conv.is_read !== false,
            lastReadTimestamp: conv.last_read_timestamp
          };
          conversations.push(conversation);
        });
        console.log(`[ChatService] Added ${conversations.length} conversations with frontend-calculated timestamps (JSONB)`);
      }

      // Parse messages from JSONB column
      if (Array.isArray(apiData.messages)) {
        apiData.messages.forEach((msg: any) => {
          const message: Message = {
            id: msg.id,
            text: msg.text,
            timestamp: this.formatTimestamp(msg.timestamp),
            isOutgoing: Boolean(msg.is_outgoing),
            status: msg.status as Message['status'],
            conversationId: msg.conversation_id
          };
          messages.push(message);
        });
        console.log(`[ChatService] Added ${messages.length} messages from JSONB`);
      }

      // Sort conversations: unread first, then by timestamp desc
      const sortedConversations = conversations.sort((a, b) => {
        if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
        if (a.unreadCount === 0 && b.unreadCount > 0) return 1;
        return this.compareTimestamps(b.timestamp, a.timestamp);
      });

      console.log('[ChatService] PostgreSQL json_build_object structure parsing completed (JSONB)');

      return {
        success: true,
        conversations: sortedConversations,
        messages
      };
    }

    // Handle direct response format: {response: {data: {conversations, messages}}}
    if (rawData.response && rawData.response.data && 
        rawData.response.data.conversations && rawData.response.data.messages) {
      
      console.log('[ChatService] Processing direct response structure (JSONB)');
      const apiData = rawData.response.data;
      
      const conversations: Conversation[] = [];
      const messages: Message[] = [];

      // Parse conversations avec calcul frontend des vrais timestamps
      if (Array.isArray(apiData.conversations)) {
        apiData.conversations.forEach((conv: any) => {
          // NOUVEAU: Calculer le vrai timestamp et message depuis les données des messages
          const realTimestamp = this.calculateRealLastTimestamp(conv.id, apiData.messages) || this.formatTimestamp(conv.last_message_timestamp);
          const realLastMessage = this.calculateRealLastMessage(conv.id, apiData.messages) || conv.last_message || 'Aucun message';
          
          console.log(`[ChatService] Timestamp comparison for ${conv.phone_number}:`, {
            apiTimestamp: conv.last_message_timestamp,
            calculatedTimestamp: realTimestamp,
            apiLastMessage: conv.last_message,
            calculatedLastMessage: realLastMessage.substring(0, 50) + '...'
          });

          const conversation: Conversation = {
            id: conv.id,
            phoneNumber: conv.phone_number,
            lastMessage: realLastMessage, // Utiliser le vrai dernier message
            timestamp: realTimestamp, // Utiliser le vrai timestamp calculé
            unreadCount: conv.unread_count || 0,
            isActive: conv.is_active || false,
            isOnline: conv.is_online || false,
            autoAiEnabled: conv.auto_ai_enabled || false,
            isRead: conv.is_read !== false,
            lastReadTimestamp: conv.last_read_timestamp
          };
          conversations.push(conversation);
        });
        console.log(`[ChatService] Added ${conversations.length} conversations with frontend-calculated timestamps (JSONB direct)`);
      }

      // Parse messages from JSONB
      if (Array.isArray(apiData.messages)) {
        apiData.messages.forEach((msg: any) => {
          const message: Message = {
            id: msg.id,
            text: msg.text,
            timestamp: this.formatTimestamp(msg.timestamp),
            isOutgoing: Boolean(msg.is_outgoing),
            status: msg.status as Message['status'],
            conversationId: msg.conversation_id
          };
          messages.push(message);
        });
        console.log(`[ChatService] Added ${messages.length} messages from JSONB direct`);
      }

      // Sort conversations: unread first, then by timestamp desc
      const sortedConversations = conversations.sort((a, b) => {
        if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
        if (a.unreadCount === 0 && b.unreadCount > 0) return 1;
        return this.compareTimestamps(b.timestamp, a.timestamp);
      });

      console.log('[ChatService] Direct response structure parsing completed (JSONB)');

      return {
        success: true,
        conversations: sortedConversations,
        messages
      };
    }

    // Handle the specific nested structure: [{response: {data: {conversations, messages}}}]
    if (Array.isArray(rawData) && rawData.length > 0) {
      const firstItem = rawData[0];
      if (firstItem.response && firstItem.response.data && 
          firstItem.response.data.conversations && firstItem.response.data.messages) {
        
        console.log('[ChatService] Processing nested API structure (JSONB)');
        const apiData = firstItem.response.data;
        
        const conversations: Conversation[] = [];
        const messages: Message[] = [];

        // Parse conversations avec calcul frontend des vrais timestamps
        if (Array.isArray(apiData.conversations)) {
          apiData.conversations.forEach((conv: any) => {
            // NOUVEAU: Calculer le vrai timestamp et message depuis les données des messages
            const realTimestamp = this.calculateRealLastTimestamp(conv.id, apiData.messages) || this.formatTimestamp(conv.last_message_timestamp);
            const realLastMessage = this.calculateRealLastMessage(conv.id, apiData.messages) || conv.last_message || 'Aucun message';
            
            console.log(`[ChatService] Timestamp comparison for ${conv.phone_number}:`, {
              apiTimestamp: conv.last_message_timestamp,
              calculatedTimestamp: realTimestamp,
              apiLastMessage: conv.last_message,
              calculatedLastMessage: realLastMessage.substring(0, 50) + '...'
            });

            const conversation: Conversation = {
              id: conv.id,
              phoneNumber: conv.phone_number,
              lastMessage: realLastMessage, // Utiliser le vrai dernier message
              timestamp: realTimestamp, // Utiliser le vrai timestamp calculé
              unreadCount: conv.unread_count || 0,
              isActive: conv.is_active || false,
              isOnline: conv.is_online || false,
              autoAiEnabled: conv.auto_ai_enabled || false,
              isRead: conv.is_read !== false,
              lastReadTimestamp: conv.last_read_timestamp
            };
            conversations.push(conversation);
          });
          console.log(`[ChatService] Added ${conversations.length} conversations with frontend-calculated timestamps (JSONB nested)`);
        }

        // Parse messages from JSONB
        if (Array.isArray(apiData.messages)) {
          apiData.messages.forEach((msg: any) => {
            const message: Message = {
              id: msg.id,
              text: msg.text,
              timestamp: this.formatTimestamp(msg.timestamp),
              isOutgoing: Boolean(msg.is_outgoing),
              status: msg.status as Message['status'],
              conversationId: msg.conversation_id
            };
            messages.push(message);
          });
          console.log(`[ChatService] Added ${messages.length} messages from JSONB nested`);
        }

        // Sort conversations: unread first, then by timestamp desc
        const sortedConversations = conversations.sort((a, b) => {
          if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
          if (a.unreadCount === 0 && b.unreadCount > 0) return 1;
          return this.compareTimestamps(b.timestamp, a.timestamp);
        });

        console.log('[ChatService] Nested structure parsing completed (JSONB)');

        return {
          success: true,
          conversations: sortedConversations,
          messages
        };
      }
    }

    // Fallback for unknown format
    console.warn('[ChatService] Unknown API response format (JSONB system), returning empty data');
    return {
      success: true,
      conversations: [],
      messages: []
    };
  }

  /**
   * Formats timestamp consistently - Updated to return ISO string for proper parsing
   */
  private formatTimestamp(timestamp: any): string {
    if (!timestamp) {
      return new Date().toISOString();
    }

    try {
      let date: Date;
      
      if (typeof timestamp === 'string') {
        date = new Date(timestamp);
      } else if (typeof timestamp === 'number') {
        // Handle both seconds and milliseconds timestamps
        date = new Date(timestamp > 1000000000000 ? timestamp : timestamp * 1000);
      } else {
        date = new Date();
      }

      if (isNaN(date.getTime())) {
        return new Date().toISOString();
      }

      return date.toISOString();
    } catch (error) {
      console.warn('[ChatService] Error formatting timestamp (JSONB):', timestamp, error);
      return new Date().toISOString();
    }
  }

  /**
   * Compares timestamps for sorting
   */
  private compareTimestamps(a: string, b: string): number {
    try {
      const timeA = new Date(a).getTime();
      const timeB = new Date(b).getTime();
      return timeA - timeB;
    } catch (error) {
      return 0;
    }
  }

  /**
   * Fetches messages and conversations from the server (JSONB system)
   */
  public async fetchMessages(): Promise<ApiResponse> {
    console.log('[ChatService] fetchMessages called (JSONB system)');
    console.log('[ChatService] Using fetch URL:', this.config.fetchUrl);
    
    try {
      const response = await this.makeApiRequest(this.config.fetchUrl, {
        method: 'GET',
      });

      console.log('[ChatService] Fetch completed successfully (JSONB system)');
      return response;
    } catch (error: any) {
      console.log('[ChatService] Fetch failed (JSONB system):', error);
      throw error;
    }
  }

  /**
   * Sends a message to the server using the JSONB system
   */
  public async sendMessage(options: SendMessageOptions): Promise<ApiResponse> {
    const validation = this.validateMessage(options.message);
    if (!validation.isValid) {
      throw new Error(validation.error);
    }

    const payload: SendManualSMSPayload = {
      phone_number: options.currentConversation.phoneNumber,
      message: options.message.trim(),
      conversation_id: options.conversationId,
      timestamp: new Date().toISOString(),
      action: 'send_manual_sms',
      user_agent: navigator.userAgent
    };

    console.log('[ChatService] Sending manual SMS with JSONB system:', payload);

    try {
      const response = await this.makeSendSMSRequest(payload);

      console.log('[ChatService] Manual SMS sent successfully (JSONB system):', response);
      
      return {
        success: true,
        message: response.message
      };
    } catch (error: any) {
      console.error('[ChatService] Failed to send manual SMS (JSONB system):', error);
      throw error;
    }
  }

  /**
   * Marks a conversation as read using the dedicated endpoint
   */
  public async markConversationAsRead(conversationId: string, phoneNumber: string): Promise<{ success: boolean; message?: string }> {
    const payload = {
      conversation_id: conversationId,
      phone_number: phoneNumber,
      action: 'conversation_opened',
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent,
      read_at: new Date().toISOString()
    };

    console.log('[ChatService] Marking conversation as read (JSONB):', conversationId);
    console.log('[ChatService] Using dedicated mark as read endpoint:', this.config.markAsReadUrl);

    try {
      const response = await this.makeMarkAsReadRequest(payload);
      console.log('[ChatService] Conversation marked as read successfully (JSONB):', response);
      return response;
    } catch (error: any) {
      console.error('[ChatService] Failed to mark conversation as read (JSONB):', error);
      throw error;
    }
  }

  /**
   * Toggles Auto AI for a conversation via API
   */
  public async toggleAutoAIRemote(conversationId: string, phoneNumber: string, currentState: boolean): Promise<{ success: boolean; message?: string; newState: boolean }> {
    const newState = !currentState;
    
    const payload: ToggleAutoAIPayload = {
      conversation_id: conversationId,
      phone_number: phoneNumber,
      auto_ai_enabled: newState,
      action: 'toggle_auto_ai',
      timestamp: new Date().toISOString(),
      user_agent: navigator.userAgent
    };

    console.log('[ChatService] Toggling Auto AI via API (JSONB):', {
      conversationId,
      phoneNumber,
      currentState,
      newState
    });

    try {
      const response = await this.makeAutoAIRequest(payload);
      console.log('[ChatService] Auto AI toggled successfully (JSONB):', response);
      return {
        success: true,
        message: response.message || `Auto AI ${newState ? 'activé' : 'désactivé'}`,
        newState
      };
    } catch (error: any) {
      console.error('[ChatService] Failed to toggle Auto AI (JSONB):', error);
      throw error;
    }
  }

  /**
   * Updates a conversation's auto AI setting with API call
   */
  public async toggleAutoAI(conversations: Conversation[], conversationId: string): Promise<{
    updatedConversations: Conversation[];
    success: boolean;
    error?: string;
  }> {
    const conversation = this.getConversationById(conversations, conversationId);
    
    if (!conversation) {
      return {
        updatedConversations: conversations,
        success: false,
        error: 'Conversation introuvable'
      };
    }

    // 1. Update local state immediately for instant UX
    const updatedConversations = conversations.map((conv: Conversation) =>
      conv.id === conversationId
        ? { ...conv, autoAiEnabled: !conv.autoAiEnabled }
        : conv
    );

    try {
      // 2. Send to server
      await this.toggleAutoAIRemote(
        conversationId,
        conversation.phoneNumber,
        conversation.autoAiEnabled || false
      );

      return {
        updatedConversations,
        success: true
      };
    } catch (error: any) {
      console.error('[ChatService] Auto AI toggle failed, reverting local state (JSONB):', error);
      
      // Revert local state on error
      return {
        updatedConversations: conversations,
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Updates conversation after marking as read - OPTIMIZED for instant update
   */
  public updateConversationAsRead(conversations: Conversation[], conversationId: string): Conversation[] {
    const now = new Date().toISOString();
    
    // OPTIMIZED: Find index directly for better performance
    const conversationIndex = conversations.findIndex(conv => conv.id === conversationId);
    
    if (conversationIndex === -1) {
      return conversations;
    }
    
    // Clone array minimally - only the changed item
    const updatedConversations = [...conversations];
    updatedConversations[conversationIndex] = {
      ...conversations[conversationIndex],
      isRead: true,
      unreadCount: 0,
      lastReadTimestamp: now
    };
    
    return updatedConversations;
  }

  /**
   * Updates message status (for optimistic UI updates)
   */
  public updateMessageStatus(messages: Message[], messageId: string, status: Message['status']): Message[] {
    return messages.map((msg: Message) =>
      msg.id === messageId
        ? { ...msg, status }
        : msg
    );
  }

  /**
   * Updates conversation's last message (for optimistic UI updates)
   */
  public updateConversationLastMessage(
    conversations: Conversation[], 
    conversationId: string, 
    message: Message
  ): Conversation[] {
    return conversations.map((conv: Conversation) =>
      conv.id === conversationId
        ? {
            ...conv,
            lastMessage: message.text,
            timestamp: message.timestamp
          }
        : conv
    );
  }


  /**
   * Gets messages for a specific conversation
   */
  public getConversationMessages(messages: Message[], conversationId: string): Message[] {
    return messages
      .filter((msg: Message) => msg.conversationId === conversationId)
      .sort((a, b) => this.compareTimestamps(a.timestamp, b.timestamp));
  }

  /**
   * Gets conversation by ID
   */
  public getConversationById(conversations: Conversation[], conversationId: string): Conversation | undefined {
    return conversations.find((conv: Conversation) => conv.id === conversationId);
  }

  /**
   * Gets unread conversations count
   */
  public getUnreadConversationsCount(conversations: Conversation[]): number {
    return conversations.filter(conv => conv.unreadCount > 0).length;
  }

  /**
   * Gets total unread messages count
   */
  public getTotalUnreadMessagesCount(conversations: Conversation[]): number {
    return conversations.reduce((total, conv) => total + conv.unreadCount, 0);
  }

  /**
   * Returns the default ChatServiceConfig using n8n webhook URLs.
   * All endpoints are mapped to the temp-n8n.tc-automation.fr webhooks.
   * UPDATED: refreshInterval set to 30 seconds
   */
  public static createDefaultConfig(): ChatServiceConfig {
    return {
      // Not used for main chat endpoints, kept for compatibility
      fetchUrl: import.meta?.env?.VITE_FETCH_URL || 'https://sms-back.cigarette-electronique-tournefeuille.fr/webhook/get-sms-list',
      // Mark conversation as read
      markAsReadUrl: import.meta?.env?.VITE_MARK_AS_READ_URL || 'https://sms-back.cigarette-electronique-tournefeuille.fr/webhook/conversation-opened',
      // Toggle auto AI
      autoAIUrl: import.meta?.env?.VITE_AUTO_AI_URL || 'https://sms-back.cigarette-electronique-tournefeuille.fr/webhook/toggle-auto-ai',
      // Send manual SMS
      sendSMSUrl: import.meta?.env?.VITE_SEND_SMS_URL || 'https://sms-back.cigarette-electronique-tournefeuille.fr/webhook/send-manual-sms',
      apiTimeout: parseInt(import.meta?.env?.VITE_API_TIMEOUT) || 15000,
      refreshInterval: parseInt(import.meta?.env?.VITE_AUTO_REFRESH_INTERVAL) || 30000 // UPDATED: 30 seconds
    };
  }
}

// SecuredChatService: extends ChatService, adds password support for secure endpoints
export class SecuredChatService extends ChatService {
  private password: string;

  constructor(config: ChatServiceConfig, password: string) {
    super(config);
    this.password = password;
    console.log('[SecuredChatService] Initialized with authentication for JSONB system');
  }

  public setPassword(password: string) {
    this.password = password;
    console.log('[SecuredChatService] Password updated for secure requests');
  }

  /**
   * Override getSecuredHeaders to add X-Auth-Password header
   */
  protected getSecuredHeaders(): Record<string, string> {
    const baseHeaders = super.getSecuredHeaders();
    return {
      ...baseHeaders,
      'X-Auth-Password': this.password,
    };
  }
}